/**
 * The SLA chip on a queue row (#217): breached, due within the hour, paused, or the time left on the nearer target.
 * Reads the stored clock (`CaseRow.sla`, last sweep); nothing here recomputes it. Missing `sla` renders nothing
 * (SLA off or not swept yet), never an invented "on time".
 */
import { Chip, type ChipTone } from '@patchbay/ui'
import type { SlaFacts } from '../../lib/api/generated'
import { formatWhen } from '../../lib/display-time'

function short(iso: string): string {
  return formatWhen(iso, { style: 'absolute' })
}

export function slaChip(sla: SlaFacts | null | undefined): { label: string; tone: ChipTone; title: string } | null {
  if (!sla) return null
  const prio = sla.priority_source === 'default' ? `${sla.priority} (default: Desk priority unknown)` : sla.priority
  const responseOwed = !sla.responded_at
  const due = responseOwed ? sla.response_due_at : sla.resolution_due_at
  const which = responseOwed ? 'response' : 'resolution'
  switch (sla.state) {
    case 'breached': {
      const missed = responseOwed && sla.response_breached_at ? 'response' : 'resolution'
      const at = missed === 'response' ? sla.response_breached_at : sla.resolution_breached_at
      return {
        label: `SLA breached · ${missed}`,
        tone: 'danger',
        title: `${prio}: past the ${missed} target${at ? ` since ${short(at)}` : ''}`,
      }
    }
    case 'due_soon':
      return {
        label: `SLA ${which} due ${due ? short(due) : 'soon'}`,
        tone: 'warning',
        title: `${prio}: the ${which} target falls due within the hour`,
      }
    case 'paused':
      return { label: 'SLA paused', tone: 'neutral', title: `${prio}: the clock is paused by the Desk status` }
    case 'ok':
      return due ? { label: `SLA ${which} ${short(due)}`, tone: 'outline', title: `${prio}: ${which} due ${short(due)}` } : null
    default:
      return null // met / missed: a closed case shows no chip in the queue
  }
}

export function SlaChip({ sla }: { sla: SlaFacts | null | undefined }) {
  const chip = slaChip(sla)
  if (!chip) return null
  return (
    <Chip tone={chip.tone} title={chip.title}>
      {chip.label}
    </Chip>
  )
}
